import React, { useState, useRef, Fragment } from 'react';
import axios from 'axios';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAppDispatch } from 'app/hooks';
import { fetchAccounts, fetchOauthIntegrations } from 'features/snowflake-accounts/reducers/snowflakeAccountsSlice';

export enum ObjectType {
  ACCOUNT = 'account',
  INTEGRATION = 'integration',
}
interface DeleteConfirmationDialogProps {
  open: boolean;
  setOpen: any;
  accountId: number;
  type: ObjectType;
}

const DeleteConfirmationDialog = (props: DeleteConfirmationDialogProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const cancelButtonRef = useRef(null);

  const deleteUrl =
    props.type === ObjectType.ACCOUNT
      ? `/api/v1/snowflake/account/${props.accountId}`
      : `/api/v1/snowflake/oauth-integration/${props.accountId}`;

  const toDispatch = props.type === ObjectType.ACCOUNT ? fetchAccounts : fetchOauthIntegrations;

  const objectText = props.type === ObjectType.ACCOUNT ? 'account' : 'integration';

  const onDelete = (e: React.FormEvent): void => {
    e.preventDefault();
    setLoading(true);
    setFailed(false);
    axios
      .delete(deleteUrl)
      .then((response) => {
        dispatch(toDispatch()).catch((err) => err);
        onClose();
      })
      .catch((err) => {
        console.log(err);
        setFailed(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onClose = (): void => {
    props.setOpen(false);
    setLoading(false);
    setFailed(false);
  };

  return (
    <Transition.Root show={props.open} as={Fragment}>
      <Dialog as="div" className="relative z-10" initialFocus={cancelButtonRef} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <Dialog.Title as="h3" className="text-base  leading-6 text-gray-900">
                      Remove {objectText}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">Are you sure you want to remove your {objectText}?</p>
                    </div>
                  </div>
                </div>
                {loading && !failed && (
                  <div className="my-2 w-full flex items-center justify-center">
                    <div
                      className="animate-spin inline-block w-6 h-6 border-[2px] border-current border-t-transparent text-cyan-800 rounded-full"
                      role="status"
                      aria-label="loading"
                    >
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                )}
                {failed && (
                  <div className="my-2 mx-[auto] text-sm w-3/4 rounded bg-red-50 border-2 border-red-500 flex items-center justify-center">
                    <a className="font-medium text-red-600 hover:text-red-500">Failed to delete {objectText}</a>
                  </div>
                )}
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm  text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                    onClick={onDelete}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm  text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={() => onClose()}
                    ref={cancelButtonRef}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default DeleteConfirmationDialog;
